import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { CreateBespokeEnquiry } from '../features/bespokeEnquiry/BespokeEnquirySlice';

const styles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
    zIndex: 2147483647,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px'
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column'
  },
  modalContent: {
    padding: '40px',
    overflowY: 'auto',
    flex: 1
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: '8px'
  },
  description: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '24px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151'
  },
  input: {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    transition: 'border-color 0.2s',
    outline: 'none'
  },
  inputError: {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #ef4444',
    fontSize: '14px',
    transition: 'border-color 0.2s',
    outline: 'none'
  },
  errorMessage: {
    color: '#ef4444',
    fontSize: '12px',
    marginTop: '4px'
  },
  textarea: {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    transition: 'border-color 0.2s',
    outline: 'none',
    minHeight: '100px',
    resize: 'vertical'
  },
  button: {
    padding: '14px 28px',
    borderRadius: '12px',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  primaryButton: {
    background: 'linear-gradient(to right, #2563eb, #4f46e5)',
    color: '#ffffff',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
  },
  cancelButton: {
    background: '#f9fafb',
    color: '#111827',
    border: '2px solid #e5e7eb'
  },
  required: {
    color: '#ef4444'
  }
};

const BespokeEnquiryModal = ({ isOpen, onClose, companyId, onSuccess }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email_address: '',
    phone_number: '',
    number_of_active_students: '',
    number_of_teachers: '',
    number_of_locations: '',
    brief_description: '',
    expected_go_live_date: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }
    if (!formData.contact_name.trim()) {
      newErrors.contact_name = 'Contact name is required';
    }
    if (!formData.email_address.trim()) {
      newErrors.email_address = 'Email address is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email_address)) {
      newErrors.email_address = 'Invalid email format';
    }
    if (!formData.phone_number.trim()) {
      newErrors.phone_number = 'Phone number is required';
    }
    if (!formData.number_of_active_students) {
      newErrors.number_of_active_students = 'Number of active students is required';
    }
    if (!formData.number_of_teachers) {
      newErrors.number_of_teachers = 'Number of teachers is required';
    }
    if (!formData.number_of_locations) {
      newErrors.number_of_locations = 'Number of locations is required';
    }
    if (!formData.brief_description.trim()) {
      newErrors.brief_description = 'Brief description is required';
    }
    if (!formData.expected_go_live_date) {
      newErrors.expected_go_live_date = 'Expected go-live date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    const response = await dispatch(CreateBespokeEnquiry({
      company_id: companyId,
      ...formData
    }));
    
    if (response?.payload?.code === 1) {
      setFormData({
        company_name: '',
        contact_name: '',
        email_address: '',
        phone_number: '',
        number_of_active_students: '',
        number_of_teachers: '',
        number_of_locations: '',
        brief_description: '',
        expected_go_live_date: ''
      });
      setErrors({});
      //toast.success(response?.payload?.message || 'Enquiry submitted successfully!');
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } else {
      toast.error(response?.payload?.message || 'Failed to submit enquiry. Please try again.');
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={styles.backdrop}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            style={styles.modal}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalContent}>
            <motion.h2
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              style={styles.title}
            >
              Enquire For Bespoke Solution
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              style={styles.description}
            >
              Please provide your details and we'll get back to you with a custom solution tailored to your needs.
            </motion.p>

            <motion.form
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              style={styles.form}
              onSubmit={handleSubmit}
            >
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Company Name <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  style={errors.company_name ? styles.inputError : styles.input}
                  placeholder="Enter your company name"
                />
                {errors.company_name && <span style={styles.errorMessage}>{errors.company_name}</span>}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Contact Name <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleChange}
                  style={errors.contact_name ? styles.inputError : styles.input}
                  placeholder="Enter contact person name"
                />
                {errors.contact_name && <span style={styles.errorMessage}>{errors.contact_name}</span>}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Email Address <span style={styles.required}>*</span>
                </label>
                <input
                  type="email"
                  name="email_address"
                  value={formData.email_address}
                  onChange={handleChange}
                  style={errors.email_address ? styles.inputError : styles.input}
                  placeholder="Enter email address"
                />
                {errors.email_address && <span style={styles.errorMessage}>{errors.email_address}</span>}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Phone Number <span style={styles.required}>*</span>
                </label>
                <input
                  type="tel"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleChange}
                  style={errors.phone_number ? styles.inputError : styles.input}
                  placeholder="Enter phone number"
                />
                {errors.phone_number && <span style={styles.errorMessage}>{errors.phone_number}</span>}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Number of Active Students <span style={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  name="number_of_active_students"
                  value={formData.number_of_active_students}
                  onChange={handleChange}
                  style={errors.number_of_active_students ? styles.inputError : styles.input}
                  placeholder="Enter number of active students"
                  min="0"
                />
                {errors.number_of_active_students && <span style={styles.errorMessage}>{errors.number_of_active_students}</span>}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Number of Teachers/Instructors <span style={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  name="number_of_teachers"
                  value={formData.number_of_teachers}
                  onChange={handleChange}
                  style={errors.number_of_teachers ? styles.inputError : styles.input}
                  placeholder="Enter number of teachers"
                  min="0"
                />
                {errors.number_of_teachers && <span style={styles.errorMessage}>{errors.number_of_teachers}</span>}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Number of Locations <span style={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  name="number_of_locations"
                  value={formData.number_of_locations}
                  onChange={handleChange}
                  style={errors.number_of_locations ? styles.inputError : styles.input}
                  placeholder="Enter number of locations"
                  min="0"
                />
                {errors.number_of_locations && <span style={styles.errorMessage}>{errors.number_of_locations}</span>}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Brief Description of Requirements <span style={styles.required}>*</span>
                </label>
                <textarea
                  name="brief_description"
                  value={formData.brief_description}
                  onChange={handleChange}
                  style={errors.brief_description ? styles.inputError : styles.textarea}
                  placeholder="Describe your specific requirements"
                />
                {errors.brief_description && <span style={styles.errorMessage}>{errors.brief_description}</span>}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Expected Go-Live Date <span style={styles.required}>*</span>
                </label>
                <input
                  type="date"
                  name="expected_go_live_date"
                  value={formData.expected_go_live_date}
                  onChange={handleChange}
                  style={errors.expected_go_live_date ? styles.inputError : styles.input}
                />
                {errors.expected_go_live_date && <span style={styles.errorMessage}>{errors.expected_go_live_date}</span>}
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={loading}
                  style={{ ...styles.button, ...styles.primaryButton, flex: 1 }}
                >
                  {loading ? (
                    <>
                      <i className="fa fa-spinner fa-spin"></i>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-paper-plane"></i>
                      Submit Enquiry
                    </>
                  )}
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  style={{ ...styles.button, ...styles.cancelButton, flex: 1 }}
                >
                  Cancel
                </motion.button>
              </div>
            </motion.form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default BespokeEnquiryModal;
